import { ENV } from "@/env";
import { LocalFeatureFlagProvider } from "@/services/featureflags/LocalFeatureFlagProvider";
import { FliptWebProvider } from "@openfeature/flipt-web-provider";
import { ErrorCode, MultiProvider, OpenFeature, ResolutionDetails } from "@openfeature/react-sdk";

type FliptFetcherOptions = {
  etag?: string;
};
/**
 * TODO: #4169
 * We override the FLIPT fetcher so we don't send the x-flipt-environment header
 * This header is not supported on the FLIPT v1 server and would cause request rejection
 *
 */
export const createLegacyFliptFetcher = (namespace: string, baseUrl: string, clientToken: string) => {
  if (!baseUrl) {
    throw new Error("baseUrl is required for Flipt fetcher; check ENV.FLIPT_URL");
  }
  const normalizedBaseUrl = baseUrl.replace(/\/$/, "");
  const encodedNamespace = encodeURIComponent(namespace);
  const url = `${normalizedBaseUrl}/internal/v1/evaluation/snapshot/namespace/${encodedNamespace}`;

  return async (args?: FliptFetcherOptions): Promise<Response> => {
    const headers: Record<string, string> = {
      Accept: "application/json",
    };

    if (clientToken) {
      headers.Authorization = `Bearer ${clientToken}`;
    }

    if (args?.etag) {
      headers["If-None-Match"] = args.etag;
    }

    return fetch(url, {
      method: "GET",
      headers,
    });
  };
};

export const resolveEvaluation = <T>(flagKey: string, defaultValue: T): ResolutionDetails<T> => {
  const localFlagValue = getLocalFlagValue<T>(flagKey);

  if (localFlagValue == undefined) {
    // No override provided
    return {
      value: defaultValue,
      variant: "default",
      reason: "UNKNOWN",
      errorCode: ErrorCode.FLAG_NOT_FOUND,
      errorMessage: "No override provided for flag",
    };
  }

  return {
    value: localFlagValue,
    variant: "override",
    reason: "STATIC",
  };
};

export const getLocalFlagValue = <T>(flagKey: string): T | undefined => {
  if (!ENV.FEATURE_FLAGS_OVERRIDE) {
    // No override string provided
    return undefined;
  }

  try {
    const overrides = JSON.parse(ENV.FEATURE_FLAGS_OVERRIDE);

    // Check if the flagKey exists in the parsed overrides
    if (Object.prototype.hasOwnProperty.call(overrides, flagKey)) {
      const value = overrides[flagKey];
      return value as T;
    }
  } catch (error) {
    console.error("Error parsing FEATURE_FLAGS_OVERRIDE JSON:", error);
  }

  return undefined;
};

export const FLIPT_PROXY_PATH = "/flipt";

export const initializeFlipt = () => {
  if (!ENV.FLIPT_URL || !ENV.FLIPT_NAMESPACE || !ENV.FLIPT_CLIENTTOKEN) return;

  // nginx proxies this path through to ENV.FLIPT_URL, which keeps the call same-origin and moves
  // the egress off the user's network. The fetcher below builds its own request URL and ignores
  // the provider's, so it has to be handed the proxy as well or the browser still calls Flipt
  // directly.
  const fliptUrl = new URL(FLIPT_PROXY_PATH, window.location.origin).toString();

  // Configure Flipt feature flags
  OpenFeature.setContext({ targetingKey: "capture-frontend" });
  OpenFeature.setProvider(
    new MultiProvider([
      {
        // Flipt provider
        provider: new FliptWebProvider(ENV.FLIPT_NAMESPACE, {
          url: fliptUrl,
          authentication: {
            clientToken: ENV.FLIPT_CLIENTTOKEN,
          },
          fetcher: createLegacyFliptFetcher(ENV.FLIPT_NAMESPACE, fliptUrl, ENV.FLIPT_CLIENTTOKEN),
        }),
      },
      {
        // Local provider
        provider: new LocalFeatureFlagProvider(),
      },
    ]),
  );
};
